import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, GraduationCap, FileText, Target, Users, CheckCircle,
  Briefcase, Quote, ChevronLeft, ChevronRight, FolderOpen, BarChart3,
  Clock, Brain, Zap, Compass, MapPin, TrendingUp, ClipboardList,
  Globe, HeartHandshake
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { ProofBar } from '../components/ProofBar';
import { analytics } from '../hooks/useAnalytics';

const HERO_IMG = "https://images.unsplash.com/photo-1607874846377-695fd1e2aeae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxpdnklMjBsZWFndWUlMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYXV0dW1ufGVufDB8fHx8MTc3Mzg4Nzc1M3ww&ixlib=rb-4.1.0&q=85";
const GRAD_IMG = "https://images.unsplash.com/photo-1761781342506-821be95168c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxjb2xsZWdlJTIwZ3JhZHVhdGlvbiUyMGNlcmVtb255JTIwY2FwcyUyMGdvd25zJTIwY2VsZWJyYXRpb258ZW58MHx8fHwxNzc0Mzg4MTExfDA&ixlib=rb-4.1.0&q=85";

/* Animated Section wrapper */
function AnimSection({ children, className = '', animation = 'scroll-fade-up', ...props }) {
  const [ref, isVisible] = useScrollReveal(0.12);
  return (
    <div ref={ref} className={`${animation} ${isVisible ? 'is-visible' : ''} ${className}`} {...props}>
      {children}
    </div>
  );
}

function StaggerGrid({ children, className = '' }) {
  const [ref, isVisible] = useScrollReveal(0.1);
  return (
    <div ref={ref} className={`stagger-children ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

const portalFeatures = [
  { icon: FileText, title: "Essay Studio", desc: "Draft, revise, and get AI feedback. Track versions. Never lose a draft." },
  { icon: BarChart3, title: "College Match Score", desc: "AI analyzes your fit for each school. Know where you stand." },
  { icon: FolderOpen, title: "Document Vault", desc: "Transcripts, scores, rec letters — one secure place." },
  { icon: Target, title: "School List Builder", desc: "Reaches, targets, safeties. Deadlines tracked automatically." },
  { icon: Brain, title: "AI Essay Feedback", desc: "Instant brainstorming, structural improvement, grammar review." },
  { icon: Clock, title: "Application Timeline", desc: "Every deadline, task, milestone — organized and tracked." },
  { icon: Users, title: "Recommender Tracker", desc: "Manage letters, status, and follow-up reminders." },
  { icon: Zap, title: "Activity Organizer", desc: "Log extracurriculars in Common App format. Optimized." },
];

const consultingTiers = [
  {
    tier: "Start", name: "The Platform", price: "$99/yr",
    bestFor: "Self-directed families who need structure, not hand-holding.",
    points: ["Full Ivy League Admissions Portal access", "45-minute strategy session included", "Essays, deadlines, school list, documents — one system"],
    to: "/sign-up", cta: "Start the Platform", event: "platform",
  },
  {
    tier: "Tier 1", name: "Strategic Kickstart", price: "From $500",
    bestFor: "Families who need clarity and a concrete game plan.",
    points: ["90-minute strategy deep-dive", "Academic + extracurricular audit", "Deliverable: a written Admissions Game Plan"],
    to: "/consulting", cta: "See Details", event: "call",
  },
  {
    tier: "Tier 2", name: "Competitive Edge", price: "From $1,000",
    bestFor: "Strong students who need sharper positioning.",
    points: ["Everything in Strategic Kickstart", "Narrative shaping + activities positioning", "Personal brand and competition strategy"],
    to: "/consulting", cta: "See Details", event: "call",
  },
  {
    tier: "Tier 3", name: "Elite / Comprehensive", price: "By application",
    bestFor: "Families who want managed execution, end to end.",
    points: ["Full essay architecture across every school", "Both mentors + lead strategist, ongoing", "Limited to ~50 families per year"],
    to: "/consulting#elite", cta: "Apply for Elite", event: "elite", featured: true,
  },
];

const testimonials = [
  {
    quote: "I had a 3.4 GPA. Not exactly Ivy material on paper. But the portal forced me to get organized — every essay, every activity, every deadline in one place. My applications were tighter than kids with 4.0s. I got into USC, Boston University, and Northeastern.",
    name: "Aiden R.", detail: "Student — USC '29",
  },
  {
    quote: "We were drowning in spreadsheets, sticky notes, and email chains with the school counselor. The portal replaced all of it. Our son applied to 12 schools and we never missed a single deadline. The structure alone was worth ten times the $99.",
    name: "Rachel K.", detail: "Parent — Son admitted to Harvard '28",
  },
  {
    quote: "The AI essay feedback was a game-changer. I'd write a draft at midnight, get instant structural feedback, then revise before my advisor even saw it. By the time we met, we were polishing — not starting from scratch.",
    name: "Maya S.", detail: "Student — Columbia '29",
  },
  {
    quote: "My daughter started using the portal junior year. By the time senior fall hit, she had her school list mapped, essays drafted, and activities organized in Common App format. While her classmates were scrambling, she was calm.",
    name: "David P.", detail: "Parent — Daughter admitted to Brown '28",
  },
  {
    quote: "I transferred from community college. Nobody tells transfer students how to organize this process. The portal treated me like every other applicant — with structure and clarity. I got into UMich and UVA.",
    name: "Carlos M.", detail: "Transfer Student — UMich '28",
  },
];

const freeTools = [
  { icon: MapPin, title: "AnyZip Code", desc: "How geography shapes opportunity — and how to outmaneuver it.", to: "/zip-code" },
  { icon: Compass, title: "AnyPath", desc: "Transfer, state school, and non-traditional routes to elite outcomes.", to: "/elite-strategy" },
  { icon: TrendingUp, title: "Career Prep", desc: "Labor-market data and career pathways behind every major.", to: "/career-portal" },
];

export default function LandingPage() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = useCallback(() => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  }, []);

  const prevTestimonial = useCallback(() => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextTestimonial, 7000);
    return () => clearInterval(timer);
  }, [nextTestimonial]);

  const handleTestimonialNav = (action, index) => {
    analytics.testimonialExpand(index);
    action();
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <SEO
        title="Ivy League Consulting — Elite Admissions Strategy & Ivy Mentors"
        description="Ivy League admissions consulting for average students who want a real shot. Every client is paired 1-on-1 with a current Ivy League student mentor and a career specialist. 200+ Ivy placements, $38M+ in scholarships."
        keywords="ivy league consulting, ivy league admissions, ivy league student mentor, college admissions strategy, harvard yale princeton admissions help, elite college consulting"
      />

      <MarketingNav />

      {/* ─── HERO ─── */}
      <section className="pt-16 relative overflow-hidden">
        <div className="relative h-[82vh] min-h-[580px] max-h-[820px]">
          <img src={HERO_IMG} alt="Ivy League campus" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F392B] via-[#0F392B]/75 to-[#0F392B]/35" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 mb-6 backdrop-blur-sm animate-[fadeIn_1s_ease-out]">
                <span className="text-xs font-medium text-white/90 tracking-wide uppercase">A Managed Admissions Operating System</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-white mb-6 animate-[fadeIn_1.2s_ease-out]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="hero-headline">
                Turn elite admissions from guesswork into a <span className="italic text-[#D4AF37]">managed strategy.</span>
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed mb-9 animate-[fadeIn_1.4s_ease-out]" data-testid="hero-subheadline">
                Every student is paired with a <span className="text-white font-medium">current Ivy League student in their intended major</span>, a <span className="text-white font-medium">working professional in their target career</span>, and the <span className="text-white font-medium">Ivy League Admissions Portal</span> — one system that manages essays, deadlines, school strategy, and accountability.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center animate-[fadeIn_1.6s_ease-out]">
                <Link to="/sign-up" onClick={() => analytics.ctaPlatformStart('hero')}>
                  <Button size="lg" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-13 px-8 text-base font-semibold shadow-xl" data-testid="hero-cta-platform">
                    Start the $99 Platform <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/consultation" onClick={() => analytics.ctaStrategyCall('hero')}>
                  <Button size="lg" className="bg-white text-[#0F392B] hover:bg-white/90 h-13 px-8 text-base font-semibold shadow-xl" data-testid="hero-cta-call">
                    Book a Strategy Call
                  </Button>
                </Link>
                <Link to="/consulting#elite" onClick={() => analytics.ctaEliteApply('hero')}>
                  <Button size="lg" variant="outline" className="h-13 px-8 text-base border-white/30 text-white hover:bg-white/10" data-testid="hero-cta-elite">
                    Apply for Elite Consulting
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROOF BAR (first viewport) ─── */}
      <ProofBar location="home-hero" />

      {/* ─── PROBLEM FRAMING ─── */}
      <section className="py-16 md:py-20" data-testid="problem-section">
        <AnimSection className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">The Problem</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            The process is fragmented <span className="italic">by design.</span>
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-4">
            Eleven school portals. A counselor responsible for four hundred students. Essay drafts scattered across five Google Docs. Deadlines living in someone's head. For most families, the stakes are the highest they will ever face — and the system is improvised.
          </p>
          <p className="text-base text-foreground leading-relaxed font-medium">
            We replace improvisation with a managed process: one strategy, one platform, named mentors, and a clear sequence from first list to final decision.
          </p>
        </AnimSection>
      </section>

      {/* ─── OUR MISSION ─── */}
      <section className="py-16 md:py-24 bg-[#0F392B] relative overflow-hidden" data-testid="mission-section">
        <div className="absolute top-0 left-0 w-72 h-72 bg-[#D4AF37]/5 rounded-full -translate-y-1/2 -translate-x-1/3" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <AnimSection animation="scroll-fade-left">
              <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">Our Mission</p>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="mission-headline">
                Ivy League resources,<br />for the <span className="italic text-[#D4AF37]">next generation.</span>
              </h2>
              <p className="text-base text-white/80 leading-relaxed mb-4">
                For decades, the playbook to elite admissions lived behind closed doors — passed between the most connected families, the best-resourced schools, the right zip codes. We exist to take that repertoire and bring it to the average student.
              </p>
              <p className="text-base text-white/80 leading-relaxed mb-4">
                That's why every client is paired with a <span className="text-white font-medium">current Ivy League student mentor</span> — students who got in, and who see it as their mission to hand down what they learned and lift the next generation up.
              </p>
              <p className="text-base text-white font-medium leading-relaxed">
                At Ivy League Consulting, we believe bridging that gap isn't a business model. It's our responsibility.
              </p>
            </AnimSection>
            <AnimSection animation="scroll-fade-right">
              <div className="space-y-4">
                {[
                  { icon: HeartHandshake, title: "Mentors with a mission", desc: "Our Ivy League student mentors don't gatekeep what got them in. They join because they believe in lifting the students coming up behind them." },
                  { icon: Globe, title: "Across the country and the world", desc: "Mentors on staff coast to coast in the U.S. and around the world — matched to each student's major, target schools, and ambition." },
                  { icon: GraduationCap, title: "Bridging the gap is our job", desc: "The insight once reserved for the most connected families, delivered to any student willing to do the work — regardless of school, background, or zip code." },
                ].map((p, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm" data-testid={`mission-pillar-${i}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
                        <p.icon className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-white mb-1.5">{p.title}</h3>
                        <p className="text-sm text-white/70 leading-relaxed">{p.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </AnimSection>
          </div>
        </div>
      </section>

      {/* ─── TWO PERSPECTIVES (DUAL-MENTOR) ─── */}
      <section className="py-16 md:py-24 bg-[#F9F9F7] border-y border-[#E2E2E2]" data-testid="dual-mentor-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection className="text-center mb-14">
            <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">Why Families Choose Us</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              The two perspectives missing<br />from <span className="italic">most applications.</span>
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Most firms assign one generalist advisor. We pair every client with a current Ivy League student mentor and a career specialist — mentors who share one mission: lift the next generation up.
            </p>
          </AnimSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AnimSection animation="scroll-fade-left">
              <div className="bg-[#0F392B] rounded-2xl p-8 text-white relative overflow-hidden h-full" data-testid="mentor-ivy-student">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center mb-5">
                    <GraduationCap className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.5} />
                  </div>
                  <p className="text-xs font-medium text-[#D4AF37] uppercase tracking-widest mb-2">The Ivy Student Mentor</p>
                  <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Lived school and major insight</h3>
                  <p className="text-sm text-white/70 leading-relaxed mb-5">
                    Want to study neuroscience at Penn? You work with a Penn student studying neuroscience — someone who navigated the exact process you're facing, one or two cycles ago.
                  </p>
                  <div className="space-y-3">
                    {["Knows the department culture, research access, and student life firsthand", "Reviewed the same Common App prompts just one or two years ago", "Tells you what actually matters — not what the brochure says", "On staff across the country and around the world — matched to your major and ambition"].map((item, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <CheckCircle className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" strokeWidth={2} />
                        <p className="text-xs text-white/80 leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimSection>
            <AnimSection animation="scroll-fade-right">
              <div className="bg-white border-2 border-[#0F392B] rounded-2xl p-8 relative overflow-hidden h-full" data-testid="mentor-professional">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#0F392B]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-[#0F392B]/10 flex items-center justify-center mb-5">
                    <Briefcase className="h-6 w-6 text-[#0F392B]" strokeWidth={1.5} />
                  </div>
                  <p className="text-xs font-medium text-[#0F392B] uppercase tracking-widest mb-2">The Career Mentor</p>
                  <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Adult-level career credibility</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                    Want to be a surgeon? You work with a practicing physician. Your career mentor helps you answer the question committees weigh most heavily: <span className="text-foreground font-medium">why.</span>
                  </p>
                  <div className="space-y-3">
                    {["Frames your future direction with authenticity and depth", "Makes your 'Why This Major' essays difficult to dismiss", "Lends your application a maturity 17-year-olds rarely achieve alone"].map((item, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <CheckCircle className="h-4 w-4 text-[#0F392B] mt-0.5 flex-shrink-0" strokeWidth={2} />
                        <p className="text-xs text-muted-foreground leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimSection>
          </div>
          <AnimSection className="mt-8">
            <div className="bg-white rounded-2xl border border-[#E2E2E2] p-6 md:p-8 flex flex-col md:flex-row items-center gap-6" data-testid="dual-mentor-cta-block">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <ClipboardList className="h-5 w-5 text-[#D4AF37]" />
                  <p className="text-sm font-semibold text-foreground">The third element: advisor plus platform.</p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your lead advisor sets strategy and sequencing; the <span className="text-foreground font-medium">Ivy League Admissions Portal</span> manages essays, deadlines, school fit, recommendations, and accountability — so nothing is left to memory or luck.
                </p>
              </div>
              <Link to="/consulting" onClick={() => analytics.ctaStrategyCall('dual-mentor')}>
                <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-12 px-8 text-sm font-semibold whitespace-nowrap shadow-lg" data-testid="dual-mentor-cta">
                  Explore Consulting <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ─── PLATFORM / COMMAND CENTER ─── */}
      <section className="py-16 md:py-24" data-testid="features-section">
        <AnimSection className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-14">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">The Ivy League Admissions Portal</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            A command center for the <span className="italic">entire application.</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Not a course. Not a checklist. Not a Google Drive folder. One structured system that turns scattered effort into a managed, accountable process.
          </p>
        </AnimSection>
        <StaggerGrid className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {portalFeatures.map((f, i) => (
            <div key={i} className="p-5 rounded-xl bg-[#F9F9F7] border border-[#E2E2E2] hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`feature-${i}`}>
              <div className="w-9 h-9 rounded-lg bg-[#0F392B] flex items-center justify-center mb-3">
                <f.icon className="h-4 w-4 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="text-sm font-medium mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>{f.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </StaggerGrid>
        <AnimSection className="text-center mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Link to="/sign-up" onClick={() => analytics.ctaPlatformStart('platform-section')}>
            <Button size="lg" className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-12 px-8 text-base shadow-lg" data-testid="features-cta">
              Start the $99 Platform <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/portal">
            <Button size="lg" variant="ghost" className="h-12 px-6 text-base">
              See Inside the Portal
            </Button>
          </Link>
        </AnimSection>
      </section>

      {/* ─── DIAGNOSTIC BAND ─── */}
      <section className="py-12 bg-[#0F392B] border-y border-[#D4AF37]/20" data-testid="diagnostic-band">
        <AnimSection className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-6">
          <div className="flex-1 text-center md:text-left">
            <p className="text-xs font-medium text-[#D4AF37] uppercase tracking-widest mb-2">The Ivy Signals Diagnostic</p>
            <h3 className="text-xl md:text-2xl font-medium text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Where does your application stand today?
            </h3>
            <p className="text-sm text-white/60">
              Six signals. Ten minutes. An honest read on your strongest asset, your biggest narrative risk, and the right next step.
            </p>
          </div>
          <Link to="/diagnostic">
            <Button className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-12 px-8 font-semibold whitespace-nowrap shadow-lg" data-testid="diagnostic-band-cta">
              Take the Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </AnimSection>
      </section>

      {/* ─── CONSULTING STAIRCASE ─── */}
      <section className="py-16 md:py-24 bg-[#F9F9F7] border-b border-[#E2E2E2]" data-testid="consulting-packages-section">
        <AnimSection className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-14">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">Consulting</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Choose the level of involvement<br />your family <span className="italic">requires.</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Services are framed by readiness and outcome — not package size. High-touch engagements are limited to ~50 families per year.
          </p>
        </AnimSection>
        <StaggerGrid className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {consultingTiers.map((t) => (
            <div key={t.name}
              className={t.featured
                ? 'bg-[#0F392B] rounded-2xl p-6 text-white relative ring-2 ring-[#D4AF37] shadow-2xl lg:scale-[1.03]'
                : 'bg-white rounded-2xl border border-[#E2E2E2] p-6 hover:shadow-xl transition-all duration-300'}
              data-testid={`package-${t.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-[#0F392B] text-[10px] font-bold uppercase tracking-widest rounded-full">Highest Touch</div>
              )}
              <p className={`text-[10px] font-medium uppercase tracking-widest mb-1 ${t.featured ? 'text-[#D4AF37] mt-2' : 'text-[#D4AF37]'}`}>{t.tier}</p>
              <h3 className="text-lg font-semibold mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>{t.name}</h3>
              <p className={`text-xl font-bold mb-3 ${t.featured ? 'text-[#D4AF37]' : 'text-[#0F392B]'}`} style={{ fontFamily: "'Playfair Display', serif" }}>{t.price}</p>
              <p className={`text-xs italic mb-4 leading-relaxed ${t.featured ? 'text-white/70' : 'text-muted-foreground'}`}>Best for: {t.bestFor}</p>
              <div className="space-y-2 mb-5">
                {t.points.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${t.featured ? 'text-[#D4AF37]' : 'text-[#0F392B]'}`} strokeWidth={2} />
                    <p className={`text-[11px] ${t.featured ? 'text-white/80' : 'text-muted-foreground'}`}>{item}</p>
                  </div>
                ))}
              </div>
              <Link to={t.to} onClick={() => {
                if (t.event === 'platform') analytics.ctaPlatformStart('staircase');
                else if (t.event === 'elite') analytics.ctaEliteApply('staircase');
                else analytics.ctaStrategyCall('staircase');
              }}>
                <Button className={t.featured
                  ? 'w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-[#0F392B] h-9 text-xs font-semibold'
                  : 'w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-9 text-xs'}>
                  {t.cta} <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            </div>
          ))}
        </StaggerGrid>
        <AnimSection className="text-center mt-8">
          <p className="text-xs text-muted-foreground">Every consulting tier includes full platform access. Engagement scope is confirmed on your strategy call.</p>
        </AnimSection>
      </section>

      {/* ─── RESULTS PREVIEW ─── */}
      <section className="py-16 md:py-24" data-testid="results-preview-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection className="text-center mb-12">
            <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">Results</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Outcomes, reported <span className="italic">responsibly.</span>
            </h2>
            <p className="text-base text-muted-foreground max-w-xl mx-auto">
              We don't publish hype. We publish placements, scholarship totals, and anonymized case studies — with a clear note on how each number is counted.
            </p>
          </AnimSection>
          <StaggerGrid className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {[
              { stat: "200+", title: "Ivy League placements", desc: "Across firm engagements and our mentor network — Harvard to Cornell, plus Ivy Plus peers." },
              { stat: "$38M+", title: "In scholarships secured", desc: "Cumulative merit and need-based awards reported by client families." },
              { stat: "~50", title: "Clients per year", desc: "High-touch engagements are deliberately capped. Depth over volume." },
            ].map((s, i) => (
              <div key={i} className="bg-[#F9F9F7] border border-[#E2E2E2] rounded-2xl p-7 text-center hover:shadow-lg transition-shadow" data-testid={`results-preview-${i}`}>
                <p className="text-4xl font-semibold text-[#0F392B] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{s.stat}</p>
                <p className="text-sm font-medium mb-1.5">{s.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </StaggerGrid>
          <AnimSection className="text-center">
            <Link to="/results">
              <Button variant="outline" className="h-11 px-8 text-sm border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white" data-testid="results-preview-cta">
                See Results & Methodology <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </AnimSection>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="py-16 md:py-24 bg-[#0F392B]" data-testid="testimonials-section">
        <AnimSection className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4 text-center">In Their Words</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-white text-center mb-16" style={{ fontFamily: "'Playfair Display', serif" }}>
            From every GPA, <span className="italic">every background.</span>
          </h2>
          <div className="relative">
            <div className="min-h-[220px] flex items-center">
              <div className="w-full text-center px-8 md:px-16">
                <Quote className="h-8 w-8 text-[#D4AF37]/30 mx-auto mb-6" strokeWidth={1} />
                <blockquote className="text-base md:text-lg text-white/85 leading-relaxed italic mb-8" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="testimonial-quote">
                  "{testimonials[currentTestimonial].quote}"
                </blockquote>
                <p className="text-sm font-medium text-white">{testimonials[currentTestimonial].name}</p>
                <p className="text-xs text-[#D4AF37] mt-1">{testimonials[currentTestimonial].detail}</p>
              </div>
            </div>
            <button onClick={() => handleTestimonialNav(prevTestimonial, currentTestimonial - 1)} className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="testimonial-prev-btn" aria-label="Previous">
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>
            <button onClick={() => handleTestimonialNav(nextTestimonial, currentTestimonial + 1)} className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="testimonial-next-btn" aria-label="Next">
              <ChevronRight className="h-5 w-5 text-white" />
            </button>
          </div>
          <div className="flex items-center justify-center gap-2 mt-10">
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => { analytics.testimonialExpand(i); setCurrentTestimonial(i); }} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentTestimonial ? 'bg-[#D4AF37] w-6' : 'bg-white/25 hover:bg-white/40'}`} data-testid={`testimonial-dot-${i}`} aria-label={`Testimonial ${i + 1}`} />
            ))}
          </div>
        </AnimSection>
      </section>

      {/* ─── FREE TOOLS STRIP ─── */}
      <section className="py-14 border-b border-[#E2E2E2]" data-testid="free-tools-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection className="text-center mb-8">
            <p className="text-xs font-medium text-[#D4AF37] uppercase tracking-widest mb-2">Free Research Tools</p>
            <h3 className="text-xl md:text-2xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Start with the data.</h3>
          </AnimSection>
          <StaggerGrid className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {freeTools.map((t, i) => (
              <Link key={i} to={t.to} className="group p-6 rounded-xl bg-[#F9F9F7] border border-[#E2E2E2] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 block" data-testid={`free-tool-${i}`}>
                <t.icon className="h-5 w-5 text-[#0F392B] mb-3" strokeWidth={1.5} />
                <p className="text-sm font-medium mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{t.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed mb-2">{t.desc}</p>
                <span className="text-xs font-medium text-[#0F392B] inline-flex items-center gap-1 group-hover:gap-2 transition-all">Explore <ArrowRight className="h-3 w-3" /></span>
              </Link>
            ))}
          </StaggerGrid>
        </div>
      </section>

      {/* ─── FINAL CTA — THREE PATHS ─── */}
      <section className="relative overflow-hidden" data-testid="final-cta-section">
        <div className="relative min-h-[520px]">
          <img src={GRAD_IMG} alt="Graduation ceremony" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0F392B]/90" />
          <div className="relative z-10 py-20">
            <AnimSection className="max-w-5xl mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Three ways to <span className="italic text-[#D4AF37]">begin.</span>
              </h2>
              <p className="text-base text-white/70 mb-12 max-w-lg mx-auto">
                Whichever path you choose, the goal is the same: a coherent, managed application your family controls.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-left">
                {[
                  { title: "Start the $99 Platform", desc: "The Ivy League Admissions Portal — full access, plus a 45-minute strategy session.", cta: "Start Now", to: "/sign-up", event: () => analytics.ctaPlatformStart('final-cta'), testid: "final-cta-platform" },
                  { title: "Book a Strategy Call", desc: "A confidential conversation about your profile, your goals, and the right level of engagement.", cta: "Book a Call", to: "/consultation", event: () => analytics.ctaStrategyCall('final-cta'), testid: "final-cta-call" },
                  { title: "Apply for Elite Consulting", desc: "Managed execution, both mentors, and a lead strategist. Limited to ~50 families per year.", cta: "Apply", to: "/consulting#elite", event: () => analytics.ctaEliteApply('final-cta'), testid: "final-cta-elite" },
                ].map((p, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-6 flex flex-col" data-testid={p.testid}>
                    <p className="text-base font-semibold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{p.title}</p>
                    <p className="text-xs text-white/60 leading-relaxed mb-5 flex-1">{p.desc}</p>
                    <Link to={p.to} onClick={p.event}>
                      <Button className="w-full bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-10 text-sm font-semibold">
                        {p.cta} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            </AnimSection>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
